import type { Tables } from "@/integrations/supabase/types";

// Extended booking type with joined relations from the schedule query.
//
// `linked_open_match` is a reverse-FK embed via open_matches.booking_id ->
// bookings.id. When present it means this booking is the blocker for an
// admin-created activity (e.g. "Vinnarbana lördag"). The schedule grid
// uses this to (a) render the activity title + organizer instead of the
// generic player/price label and (b) route clicks to ActivityDetailModal
// instead of BookingDetailModal.
//
// The legacy column bookings.activity_id (FK -> open_matches_v2) is a
// DIFFERENT player-driven request flow — irrelevant to this embed.
export type LinkedOpenMatch = {
  id: string;
  activity_title: string | null;
  payment_mode: string | null;
  status: string | null;
  created_by: string | null;
};

// schedule-grid-gap-1-fix: reverse-FK embed shape for league_matches.
// Mirror of LinkedOpenMatch — same normalize-at-boundary pattern (PostgREST
// returns the embed as `{...}` for 1:1 or `[{...}]` for 1:N; both shapes
// handled by getLinkedLeagueMatch in useScheduleGridData.
export type LinkedLeagueMatch = {
  id: string;
  status: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  rescheduled_date: string | null;
  rescheduled_time: string | null;
  league_id: string | null;
  home_team_id: string | null;
  away_team_id: string | null;
  leagues: { id: string; name: string | null } | null;
  home_team: { id: string; team_name: string | null } | null;
  away_team: { id: string; team_name: string | null } | null;
};

export type ScheduleBooking = Tables<"bookings"> & {
  courts: { name: string; hall_id: string } | null;
  profiles: { full_name: string | null; phone: string | null } | null;
  linked_open_match?: LinkedOpenMatch | LinkedOpenMatch[] | null;
  linked_league_match?: LinkedLeagueMatch | LinkedLeagueMatch[] | null;
};

// Court type with joined hall info (from useOrgHall)
export type CourtWithHall = Tables<"courts"> & {
  halls?: { name: string; opening_time: string; closing_time: string } | null;
};

// Customer with joined category
export type CustomerWithCategory = Tables<"customers"> & {
  customer_categories: { slug: string; name: string } | null;
};

// Training session with joined group/court
export type ScheduleTrainingSession = Tables<"training_sessions"> & {
  training_groups: { name: string; color: string | null } | null;
  courts: { name: string; hall_id: string } | null;
};

// Booking category types for color coding
export type BookingCategory = "paid" | "unpaid" | "partial" | "recurring" | "admin" | "cancelled" | "blocked" | "member_paid" | "member_unpaid" | "invoice_pending" | "invoice_paid" | "free" | "named_block";

export const CATEGORY_STYLES: Record<BookingCategory, { bg: string; border: string; label: string }> = {
  paid:             { bg: "bg-green-200/80",   border: "border-green-400",           label: "Betald" },
  unpaid:           { bg: "bg-amber-200/80",   border: "border-amber-400",           label: "Ej betald" },
  partial:          { bg: "bg-orange-200/80",  border: "border-orange-400",          label: "Delbetald" },
  recurring:        { bg: "bg-blue-200/80",    border: "border-blue-400",            label: "Abonnemang" },
  admin:            { bg: "bg-purple-200/80",  border: "border-purple-400",          label: "Klubbokning" },
  cancelled:        { bg: "bg-red-200/80",     border: "border-red-400",             label: "Avbokad" },
  blocked:          { bg: "bg-muted",          border: "border-muted-foreground/30", label: "Blockerad" },
  member_paid:      { bg: "bg-teal-200/80",    border: "border-teal-400",            label: "Medlem ✓" },
  member_unpaid:    { bg: "bg-teal-100/80",    border: "border-teal-300",            label: "Medlem" },
  invoice_pending:  { bg: "bg-sky-100/80",     border: "border-sky-400",             label: "Faktura" },
  invoice_paid:     { bg: "bg-emerald-200/80", border: "border-emerald-500",         label: "Faktura ✓" },
  free:             { bg: "bg-gray-200/80",    border: "border-gray-400",            label: "Gratis" },
  named_block:      { bg: "bg-slate-300/80",   border: "border-slate-500",           label: "Blockering" },
};

// Selection rectangle for multi-court drag selection
export interface SelectionRect {
  startCourtIdx: number;
  endCourtIdx: number;
  startSlotIdx: number;
  endSlotIdx: number;
}

// Computed selection info
export interface SelectionInfo {
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
  courtLabel: string;
  courtNames: string[];
  courtIds: string[];
  courtCount: number;
  price: { perHour: number | null; total: number } | null;
  primaryCourtId: string;
}
